import { useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { CheckIcon, CopyIcon, KeyIcon } from '@/components/icons';
import { CertificateInfoSection } from '@/components/certificates/CertificateInfoSection';
import { useCertificateDetails } from '@/hooks/data/useCertificates';
import { useClipboard } from '@/hooks/useClipboard';
import { formatDistinguishedName } from '@/utils/format';
import './CertificateDetailsModal.css';

interface CertificateDetailsModalProps {
  certificateId: string;
  commonName: string;
  onClose: () => void;
}

type TabType = 'details' | 'pem' | 'chain';

/**
 * PEM block with a copy button
 */
function PemBlock({
  label,
  pem,
  copied,
  onCopy,
}: {
  label: string;
  pem: string;
  copied: boolean;
  onCopy: () => void;
}) {
  return (
    <div className="cert-details-pem-block">
      <div className="cert-details-pem-header">
        <span className="cert-details-pem-label">{label}</span>
        <button
          type="button"
          className="cert-details-copy-btn"
          onClick={onCopy}
          title={`Copy ${label.toLowerCase()}`}
        >
          {copied ? <CheckIcon size={14} /> : <CopyIcon size={14} />}
          <span>{copied ? 'Copied!' : 'Copy'}</span>
        </button>
      </div>
      <pre className="cert-details-pem">{pem}</pre>
    </div>
  );
}

/**
 * Certificate Details Modal Component.
 * Built on ui/Modal (portal, Escape, scroll lock, focus restore).
 * Rendered only while open, so it mounts with the certificate to show.
 */
export function CertificateDetailsModal({ certificateId, commonName, onClose }: CertificateDetailsModalProps) {
  const [activeTab, setActiveTab] = useState<TabType>('details');
  const { details: data, isLoading, error } = useCertificateDetails(certificateId);
  const { copiedKey, copy } = useClipboard();

  const chain = data?.chain ?? [];
  const hasChain = chain.length > 0;

  const tabs: Array<{ id: TabType; label: string }> = [
    { id: 'details', label: 'Details' },
    { id: 'pem', label: 'PEM' },
    ...(hasChain ? [{ id: 'chain' as const, label: `Chain (${chain.length})` }] : []),
  ];

  return (
    <Modal isOpen onClose={onClose} size="xl">
      <ModalHeader>Certificate: {commonName}</ModalHeader>

      {/* Private Key Status */}
      {data && (
        <div className="cert-details-key-status-bar">
          <div className={`cert-details-key-status ${data.has_private_key ? 'has-key' : 'no-key'}`}>
            {data.has_private_key ? (
              <>
                <KeyIcon /> Private key stored (encrypted)
              </>
            ) : (
              'No private key stored'
            )}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="cert-details-tabs" role="tablist">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.id}
            className={`cert-details-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <ModalBody className="cert-details-body">
        {isLoading ? (
          <div className="cert-details-loading">
            <div className="cert-details-spinner" />
            <span>Loading certificate details...</span>
          </div>
        ) : error ? (
          <div className="cert-details-error">
            <span>{error}</span>
          </div>
        ) : data ? (
          <>
            {activeTab === 'details' && <CertificateInfoSection cert={data.certificate} />}

            {activeTab === 'pem' && (
              <div className="cert-details-pem-section">
                <PemBlock
                  label="Certificate PEM"
                  pem={data.cert_pem}
                  copied={copiedKey === 'cert'}
                  onCopy={() => copy(data.cert_pem, 'cert')}
                />
                {data.chain_pem && (
                  <PemBlock
                    label="Chain PEM"
                    pem={data.chain_pem}
                    copied={copiedKey === 'chain'}
                    onCopy={() => copy(data.chain_pem ?? '', 'chain')}
                  />
                )}
              </div>
            )}

            {activeTab === 'chain' && hasChain && (
              <div className="cert-details-chain-section">
                {chain.map((cert, index) => (
                  <CertificateInfoSection
                    key={index}
                    cert={cert}
                    title={`Certificate ${index + 1}: ${
                      cert.subject.common_name || formatDistinguishedName(cert.subject)
                    }`}
                  />
                ))}
              </div>
            )}
          </>
        ) : null}
      </ModalBody>

      <ModalFooter>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
}
